import { useEffect, useMemo, useState, useRef } from 'react';
import { fetchWithCorpId, fetchWithCorpIdAndFormData } from '../utils/api';
import Drawer from './Drawer';
import CorpForm from './CorpForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import { SortableTh, StatusBadge } from './ui/TableBits';
import { formatDate } from '../utils/dateUtils';
import { useToast } from './ui/Toast';

const EMPTY_FILTER = { name: '', address: '', city: '', state: '', zip: '', status: '' };

function CorpTable({ selectedCorp, onCorpSwitch }) {
  const toast = useToast();
  const [corps, setCorps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [failedLogos, setFailedLogos] = useState(new Set());
  const [logoUrls, setLogoUrls] = useState({});
  const fileInputRef = useRef(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);

  // Drawer state: null when closed, { mode: 'create' } or { mode: 'edit', id } when open
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (corp) => setDrawer({ mode: 'edit', id: corp.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); setReloadKey(k => k + 1); };

  // The corps endpoint has no server-side filtering, so fetch every row once
  // and filter, sort, and paginate on the client.
  useEffect(() => {
    let cancelled = false;
    const loadLogos = async (list) => {
      const urls = {};
      for (const corp of list) {
        try {
          const res = await fetchWithCorpId(`/corps/${corp.id}/logo`, { headers: { 'Accept': 'image/*' } });
          urls[corp.id] = res.ok ? URL.createObjectURL(await res.blob()) : '/default-logo.png';
        } catch { urls[corp.id] = '/default-logo.png'; }
      }
      if (!cancelled) setLogoUrls(urls);
    };
    setLoading(true);
    fetchWithCorpId('/corps?page=0&size=100000')
      .then(data => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data?.content ?? []);
        setCorps(rows);
        setError(null);
        loadLogos(rows);
      })
      .catch((e) => { if (!cancelled) { setError(e.message || 'Failed to load corporations'); setCorps([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => { setPage(0); }, [filter, size, sortConfig]);

  const filtered = useMemo(() => corps.filter(c =>
    (!filter.name || c.name?.toLowerCase().includes(filter.name.toLowerCase())) &&
    (!filter.address || c.address?.toLowerCase().includes(filter.address.toLowerCase())) &&
    (!filter.city || c.city?.toLowerCase().includes(filter.city.toLowerCase())) &&
    (!filter.state || c.state?.toLowerCase().includes(filter.state.toLowerCase())) &&
    (!filter.zip || c.zip?.toLowerCase().includes(filter.zip.toLowerCase())) &&
    (!filter.status || c.status?.toLowerCase() === filter.status.toLowerCase())
  ), [corps, filter]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    const { key, direction } = sortConfig;
    return [...filtered].sort((a, b) => {
      const vA = a[key], vB = b[key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return direction === 'asc' ? vA - vB : vB - vA;
      return direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    });
  }, [filtered, sortConfig]);

  const totalElements = sorted.length;
  const totalPages = Math.ceil(totalElements / size);
  const pageSafe = totalPages === 0 ? 0 : Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(pageSafe * size, pageSafe * size + size);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleChangePageSize = (e) => setSize(parseInt(e.target.value, 10));
  const clearFilters = () => setFilter(EMPTY_FILTER);
  const hasActiveFilters = Boolean(filter.name || filter.address || filter.city || filter.state || filter.zip || filter.status);

  const handleLogoUpload = async (corpId, file) => {
    setUploadingLogo(corpId);
    try {
      const fd = new FormData(); fd.append('logo', file);
      await fetchWithCorpIdAndFormData(`/corps/${corpId}/logo`, fd, { method: 'PUT' });
      setFailedLogos(prev => { const s = new Set(prev); s.delete(corpId); return s; });
      await new Promise(r => setTimeout(r, 500));
      setReloadKey(k => k + 1);
      toast.success('Logo updated');
    } catch (e) {
      toast.error(e.message || 'Failed to upload logo');
    } finally { setUploadingLogo(null); }
  };

  const handleLogoClick = (corpId) => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.onchange = (e) => { const f = e.target.files[0]; if (f) handleLogoUpload(corpId, f); };
      fileInputRef.current.click();
    }
  };

  const handleSwitch = (corp) => { localStorage.setItem('selectedCorpId', corp.id); onCorpSwitch(corp); };

  return (
    <div className="page-container">
      <PageHeader
        title="Corporations"
        subtitle="Manage your registered corporations"
        actions={
          <button className="btn-primary" onClick={openCreate}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Corporation
          </button>
        }
      />

      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" />

      <div className="filter-bar">
        <input name="name" placeholder="Name" value={filter.name} onChange={handleFilterChange} aria-label="Filter by name" />
        <input name="address" placeholder="Address" value={filter.address} onChange={handleFilterChange} aria-label="Filter by address" />
        <input name="city" placeholder="City" value={filter.city} onChange={handleFilterChange} aria-label="Filter by city" />
        <input name="state" placeholder="State" value={filter.state} onChange={handleFilterChange} aria-label="Filter by state" />
        <input name="zip" placeholder="Zip" value={filter.zip} onChange={handleFilterChange} aria-label="Filter by zip" />
        <select name="status" value={filter.status} onChange={handleFilterChange} aria-label="Filter by status">
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button className="btn-secondary" onClick={clearFilters}>Clear</button>
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <tr>
              <th>Logo</th>
              <SortableTh col="name" label="Name" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="address" label="Address" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="city" label="City" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="state" label="State" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="zip" label="Zip" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="status" label="Status" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="startDate" label="Start Date" sortConfig={sortConfig} onSort={handleSort} />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody style={{ opacity: loading && pageRows.length > 0 ? 0.55 : 1 }}>
            {pageRows.length === 0 ? (
              <TableState
                colSpan={9}
                loading={loading}
                error={error}
                onRetry={() => setReloadKey(k => k + 1)}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No corporations yet"
                emptyHint="Add your first corporation to get started."
                emptyAction={{ label: 'New Corporation', onClick: openCreate }}
              />
            ) : pageRows.map(corp => (
              <tr key={corp.id}>
                <td>
                  <div
                    onClick={() => handleLogoClick(corp.id)}
                    title="Click to upload logo"
                    style={{ width: '40px', height: '40px', border: '1px dashed var(--hairline)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', background: 'var(--surface-soft)' }}
                  >
                    {uploadingLogo === corp.id ? (
                      <span style={{ fontSize: '10px', color: 'var(--muted-soft)' }}>...</span>
                    ) : failedLogos.has(corp.id) ? (
                      <span style={{ color: 'var(--muted-soft)', fontSize: '18px' }}>+</span>
                    ) : (
                      <img src={logoUrls[corp.id] || '/default-logo.png'} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={() => setFailedLogos(prev => new Set([...prev, corp.id]))} />
                    )}
                  </div>
                </td>
                <td>
                  <button type="button" className="btn-link table-link" onClick={() => openEdit(corp)} title="Open corporation">
                    {corp.name}
                  </button>
                </td>
                <td>{corp.address}</td>
                <td>{corp.city}</td>
                <td>{corp.state}</td>
                <td>{corp.zip}</td>
                <td><StatusBadge status={corp.status} /></td>
                <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(corp.startDate)}</td>
                <td>
                  <button
                    onClick={() => handleSwitch(corp)}
                    className={selectedCorp?.id === corp.id ? 'badge badge-success' : 'badge badge-neutral'}
                    style={{ cursor: 'pointer', border: 'none', padding: '4px 12px', borderRadius: '20px', fontWeight: '600', fontSize: '12px' }}
                  >
                    {selectedCorp?.id === corp.id ? 'Active' : 'Switch'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? pageSafe + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(Math.max(pageSafe - 1, 0))} disabled={pageSafe === 0}>← Prev</button>
          <button onClick={() => setPage(Math.min(pageSafe + 1, totalPages - 1))} disabled={totalPages === 0 || pageSafe >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit corporation' : 'New corporation'}
        subtitle={drawer?.mode === 'edit' ? 'Update corporation info and contacts.' : 'Add a corporation in two steps: details, contacts.'}
        footer={
          drawer?.mode === 'edit' ? (
            <>
              <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
              <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
                Save changes
              </button>
            </>
          ) : (
            // Create mode is a stepper; the form renders its own step footer,
            // so the drawer only offers Cancel here.
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
          )
        }
      >
        {drawer && (
          <CorpForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            corpId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default CorpTable;
